/**
 * Cache Service
 *
 * Memory-based HTTP response caching with TTL-based expiration and LRU eviction.
 * Integrates with SSE events for automatic cache invalidation.
 *
 * Features:
 * - TTL-based expiration (configurable per entry)
 * - LRU eviction when max entries exceeded
 * - Pattern-based invalidation via RegExp
 * - SSE-driven cache invalidation
 * - Cache statistics for debugging
 * - Automatic cleanup on logout
 *
 * @see VEO-087 Sprint 2 Plan
 */
import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SSEService } from './sse.service';
import { VeoSseService } from './veo-sse.service';
import { SSEEventType } from '../models/sse-events.model';

/**
 * Cache entry with value, timestamp, and TTL
 */
interface CacheEntry<T = any> {
  value: T;
  timestamp: number;
  ttl: number;
}

/**
 * Cache statistics for debugging
 */
export interface CacheStats {
  entries: number;
  hits: number;
  misses: number;
  evictions: number;
  invalidations: number;
}

/**
 * Cache configuration
 */
interface CacheConfig {
  maxEntries: number;
  defaultTtlMs: number;
}

@Injectable({
  providedIn: 'root',
})
export class CacheService implements OnDestroy {
  private sseService = inject(SSEService);
  private veoSseService = inject(VeoSseService);

  // Cache storage
  private cache = new Map<string, CacheEntry>();

  // Configuration
  private config: CacheConfig = {
    maxEntries: 100,
    defaultTtlMs: 60000, // 60 seconds
  };

  // Statistics tracking
  private _stats = signal<CacheStats>({
    entries: 0,
    hits: 0,
    misses: 0,
    evictions: 0,
    invalidations: 0,
  });

  // Public readonly signal for stats
  readonly stats = computed(() => this._stats());

  // SSE invalidation event subjects
  private invalidateDevices$ = new Subject<void>();
  private invalidateNotifications$ = new Subject<void>();
  private invalidateScreenshots$ = new Subject<void>();

  constructor() {
    console.log('[CacheService] Initializing cache service');
    this.setupSSEInvalidation();
    this.setupDebouncedInvalidation();
  }

  /**
   * Set up debounced invalidation for SSE-driven cache clearing.
   * Prevents cache thrashing during rapid event bursts.
   */
  private setupDebouncedInvalidation(): void {
    // Debounce device status changes (100ms) before invalidating
    this.invalidateDevices$
      .pipe(debounceTime(100), takeUntilDestroyed())
      .subscribe(() => {
        this.invalidatePattern(/\/devices/);
        this.invalidatePattern(/\/veo\/devices/);
        this.invalidatePattern(/\/veo\/dashboard/);
      });

    // Debounce notification alerts (100ms)
    this.invalidateNotifications$
      .pipe(debounceTime(100), takeUntilDestroyed())
      .subscribe(() => {
        this.invalidatePattern(/\/notifications/);
      });

    // Debounce screenshot updates (100ms)
    this.invalidateScreenshots$
      .pipe(debounceTime(100), takeUntilDestroyed())
      .subscribe(() => {
        this.invalidatePattern(/\/screenshots/);
        this.invalidatePattern(/\/veo\/screenshots/);
      });
  }

  ngOnDestroy(): void {
    this.invalidateDevices$.complete();
    this.invalidateNotifications$.complete();
    this.invalidateScreenshots$.complete();
  }

  /**
   * Get cached value if valid (not expired)
   *
   * @param key Cache key
   * @returns Cached value or null if not found/expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.incrementStat('misses');
      return null;
    }

    // Check expiration
    const age = Date.now() - entry.timestamp;
    if (age > entry.ttl) {
      this.cache.delete(key);
      this.updateEntryCount();
      this.incrementStat('misses');
      return null;
    }

    this.incrementStat('hits');
    return entry.value as T;
  }

  /**
   * Set cached value with TTL
   *
   * @param key Cache key
   * @param value Value to cache
   * @param ttl Time to live in milliseconds (default: 60s)
   */
  set<T>(key: string, value: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      value,
      timestamp: Date.now(),
      ttl: ttl ?? this.config.defaultTtlMs,
    };

    this.cache.set(key, entry);
    this.updateEntryCount();

    // Check if we need to evict
    if (this.cache.size > this.config.maxEntries) {
      this.evictOldest();
    }
  }

  /**
   * Invalidate specific cache entry
   *
   * @param key Cache key to invalidate
   */
  invalidate(key: string): void {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.updateEntryCount();
      this.incrementStat('invalidations');
    }
  }

  /**
   * Invalidate all cache entries matching pattern
   *
   * @param pattern String or RegExp pattern to match keys
   */
  invalidatePattern(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let count = 0;

    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        count++;
      }
    }

    if (count > 0) {
      this.updateEntryCount();
      this.incrementStat('invalidations', count);
      console.log(`[CacheService] Invalidated ${count} entries matching pattern:`, pattern);
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    const count = this.cache.size;
    this.cache.clear();
    this.updateEntryCount();
    
    if (count > 0) {
      console.log(`[CacheService] Cleared ${count} cache entries`);
    }
  }

  /**
   * Get cache statistics
   *
   * @returns Current cache statistics
   */
  getStats(): CacheStats {
    return { ...this._stats() };
  }

  /**
   * Check if cache has valid entry for key
   *
   * @param key Cache key to check
   * @returns True if valid entry exists
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * LRU eviction - remove oldest 20% of entries
   */
  private evictOldest(): void {
    const entries = Array.from(this.cache.entries());
    
    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    // Remove oldest 20%
    const evictCount = Math.ceil(this.config.maxEntries * 0.2);
    for (let i = 0; i < evictCount && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }

    this.updateEntryCount();
    this.incrementStat('evictions', evictCount);
    console.log(`[CacheService] Evicted ${evictCount} oldest entries (LRU)`);
  }

  /**
   * Update entry count in stats
   */
  private updateEntryCount(): void {
    this._stats.update((s) => ({ ...s, entries: this.cache.size }));
  }

  /**
   * Increment specific stat counter
   */
  private incrementStat(stat: keyof Omit<CacheStats, 'entries'>, amount = 1): void {
    this._stats.update((s) => ({ ...s, [stat]: s[stat] + amount }));
  }

  /**
   * Setup SSE-driven cache invalidation
   *
   * Uses the org-filtered VeoSseService for VEO-related events.
   * The old SSEService is kept only for non-org-specific alerts/screenshots.
   * Device status changes use VeoSseService to respect org boundaries.
   */
  private setupSSEInvalidation(): void {
    // ── Old SSE (unfiltered, for non-org-specific events) ──

    // Alert events invalidate notification caches
    this.sseService
      .getAlerts()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.invalidateNotifications$.next();
      });

    // Screenshot pool events invalidate screenshot caches
    this.sseService
      .getScreenshotUpdates()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.invalidateScreenshots$.next();
      });

    // ── VEO SSE (org-filtered, for VEO dashboard events) ──

    // Device status changes — org-filtered via VeoSseService
    this.veoSseService.getDeviceStatusUpdates().pipe(takeUntilDestroyed()).subscribe(() => {
      this.invalidateDevices$.next();
    });

    // Location updates invalidate locations and devices caches
    this.veoSseService.getLocationUpdates().pipe(takeUntilDestroyed()).subscribe(() => {
      this.invalidatePattern(/\/locations/);
      this.invalidatePattern(/\/veo\/locations/);
      this.invalidatePattern(/\/veo\/devices/);
      this.invalidatePattern(/\/veo\/dashboard/);
      this.invalidatePattern(/\/monthly-summary/);
      this.invalidatePattern(/\/operational-status/);
      this.invalidatePattern(/\/performance-summary/);
      this.invalidatePattern(/\/content-delivery/);
    });

    // Route assignments invalidate route and location caches
    this.veoSseService.getRouteAssignments().pipe(takeUntilDestroyed()).subscribe(() => {
      this.invalidatePattern(/\/routes/);
      this.invalidatePattern(/\/veo\/locations/);
    });

    // Dashboard refresh events invalidate all VEO dashboard caches
    this.veoSseService.getDashboardRefresh().pipe(takeUntilDestroyed()).subscribe(() => {
      this.invalidatePattern(/\/veo\/dashboard/);
      this.invalidatePattern(/\/monthly-summary/);
      this.invalidatePattern(/\/operational-status/);
      this.invalidatePattern(/\/performance-summary/);
      this.invalidatePattern(/\/content-delivery/);
    });
  }
}

/**
 * Generate cache key from HTTP request parameters
 *
 * @param method HTTP method
 * @param url Request URL
 * @param params Optional HTTP parameters
 * @returns Cache key in format: METHOD:URL:PARAMS
 */
export function generateCacheKey(method: string, url: string, params?: HttpParams): string {
  let key = `${method}:${url}`;

  if (params) {
    // Sort params alphabetically for consistent keys
    const sortedParams: string[] = [];
    params.keys().forEach((paramKey) => {
      const values = params.getAll(paramKey);
      if (values) {
        values.forEach((value) => {
          sortedParams.push(`${paramKey}=${value}`);
        });
      }
    });
    sortedParams.sort();

    if (sortedParams.length > 0) {
      key += ':' + sortedParams.join('&');
    }
  }

  return key;
}

/**
 * Get TTL for specific URL pattern
 *
 * @param url Request URL
 * @returns TTL in milliseconds
 */
export function getTtlForUrl(url: string): number {
  // Devices endpoint - 60s
  if (url.includes('/devices/all')) {
    return 60000;
  }

  // Single device - 30s
  if (url.match(/\/devices\/[^/]+$/) && !url.includes('/all')) {
    return 30000;
  }

  // Notifications - 30s
  if (url.includes('/notifications')) {
    return 30000;
  }

  // Screenshots - 30s
  if (url.includes('/screenshots')) {
    return 30000;
  }

  // Default - 60s
  return 60000;
}
